/**
 * Menu Edit route
 * Organizer-only route for creating and editing weekly menus
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeek } from '@/contexts/WeekContext';
import { MenuEditor } from '@/components/menu/MenuEditor';
import { WeekNavigator } from '@/components/availability/WeekNavigator';
import { formatDateFull } from '@/lib/utils/date';
import { requireOrganizer } from '@/lib/auth/routeGuards';

export const Route = createFileRoute('/menu/edit')({
  beforeLoad: requireOrganizer,
  component: MenuEditComponent,
});

function MenuEditComponent() {
  const { t } = useTranslation();
  // Get user from route context - guaranteed non-null and organizer by requireOrganizer
  const { user } = Route.useRouteContext();
  const userId = user.id;

  // Use global week context
  const { weekStart, weekStartDate, goToPreviousWeek, goToNextWeek, goToToday } = useWeek();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const handleSaveSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('menu.editWeeklyMenu')}</h1>
            <p className="mt-2 text-gray-600">
              {t('menu.editMenuDescription')}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('menu.weekOf', { date: formatDateFull(weekStart) })}
            </p>
          </div>
          <Link
            to="/menu/compose"
            search={{ week: weekStartDate }}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('menu.composeMenu')}
          </Link>
        </div>

        {/* Success message */}
        {showSuccessMessage && (
          <div
            className="rounded-md bg-green-50 p-4 mb-6 animate-fade-in"
            role="alert"
          >
            <div className="flex">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  {t('menu.menuUpdatedSuccessfully')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Week navigator */}
        <WeekNavigator
          currentWeekStart={weekStart}
          onPreviousWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onToday={goToToday}
        />

        {/* Menu editor */}
        <MenuEditor
          weekStart={weekStart}
          organizerId={userId}
          onSaveSuccess={handleSaveSuccess}
        />

        {/* Help text */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-blue-400 flex-shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('menu.howToUseMenuEditor')}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>{t('menu.helpClickEmpty')}</li>
                  <li>{t('menu.helpClickChange')}</li>
                  <li>{t('menu.helpClickRemove')}</li>
                  <li>{t('menu.helpNumbersExplanation')}</li>
                  <li>{t('menu.helpWeekNavigator')}</li>
                  <li>{t('menu.helpAutoSave')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
